import {
  Text,
  GreyGrey60,
  GreyGrey80,
  IconChevronTop,
  PopoverWrapper,
  Primary13Regular,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { inflect } from 'inflection'
import { FC } from 'react'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { toAmountBasis } from 'Invoices/utils'
import {
  CurrencyTotal,
  InvoiceForTotal,
  sumByCurrency
} from './selectedInvoiceTotals'

/**
 * The combined value of the selected invoices, shown beside the bulk-action
 * counter. A single currency reads as one amount; multiple currencies can't be
 * summed into one figure, so it offers a per-currency breakdown instead.
 */
export const SelectedInvoicesTotal: FC<{ invoices: InvoiceForTotal[] }> = ({
  invoices
}) => {
  const { useCreditGrantLedger } = useFeatureFlags()

  const totals = sumByCurrency(invoices, toAmountBasis(useCreditGrantLedger))

  if (totals.length === 0) {
    return null
  }

  const onlyCurrency = totals.length === 1 ? totals[0] : undefined

  if (onlyCurrency) {
    return (
      <Text
        {...Primary13SemiBold}
        color={GreyGrey60}
        data-testid="bulkActions.summary"
      >
        {toMoney({
          value: onlyCurrency.total,
          currency: onlyCurrency.currency
        })}
      </Text>
    )
  }

  return (
    <PopoverWrapper
      trigger="click"
      placement="bottom-start"
      popover={<CurrencyBreakdown totals={totals} />}
    >
      <Flex
        as="button"
        type="button"
        alignItems="center"
        gap="4px"
        background="transparent"
        border="none"
        padding="0"
        cursor="pointer"
        data-testid="bulkActions.summary"
      >
        <Text {...Primary13SemiBold} color={GreyGrey60}>
          Multiple currencies
        </Text>
        <IconChevronTop size={16} color={GreyGrey60} />
      </Flex>
    </PopoverWrapper>
  )
}

const CurrencyBreakdown: FC<{ totals: CurrencyTotal[] }> = ({ totals }) => (
  <Flex
    flexDirection="column"
    gap="8px"
    padding="12px"
    minWidth="220px"
    data-testid="bulkActions.summary.breakdown"
  >
    {totals.map(total => (
      <Flex
        key={total.currency}
        justifyContent="space-between"
        gap="24px"
        alignItems="baseline"
      >
        <Text {...Primary13Regular} color={GreyGrey60}>
          {total.count} {total.currency} {inflect('invoice', total.count)}
        </Text>
        <Text {...Primary13SemiBold} color={GreyGrey80}>
          {toMoney({ value: total.total, currency: total.currency })}
        </Text>
      </Flex>
    ))}
  </Flex>
)
